import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CommunityClassroomSummary } from '@/types/community';

interface ClassroomCardProps {
  classroom: CommunityClassroomSummary;
  className?: string;
}

const PLACEHOLDER_AVATAR = '/teacher-placeholder.svg';

const themeStyleClasses: Record<string, { bg: string; accent: string; border: string }> = {
  clean: { bg: 'bg-white', accent: 'bg-blue-500', border: 'border-blue-200' },
  playful: { bg: 'bg-gradient-to-br from-amber-50 to-rose-50', accent: 'bg-rose-500', border: 'border-rose-200' },
  bold: { bg: 'bg-gray-900', accent: 'bg-amber-500', border: 'border-amber-400' },
  minimal: { bg: 'bg-gray-50', accent: 'bg-gray-700', border: 'border-gray-300' },
};

function getInitials(name: string) {
  const value = (name || '').trim();
  if (!value) return 'CL';
  const parts = value.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function formatUpdatedAt(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function getLocation(classroom: CommunityClassroomSummary): string | null {
  const parts = [];
  if (classroom.school) parts.push(classroom.school);
  const cityState = [classroom.city, classroom.state].filter(Boolean).join(', ');
  if (cityState) parts.push(cityState);
  return parts.length > 0 ? parts.join(' • ') : null;
}

export default function ClassroomCard({ classroom, className = '' }: ClassroomCardProps) {
  const [avatarFailed, setAvatarFailed] = useState(false);
  const themeStyle = themeStyleClasses[classroom.theme_style] || themeStyleClasses.clean;
  const avatarUrl = classroom.teacher_avatar;
  const displayName = classroom.display_name || classroom.classroom_name;
  const teacherName = classroom.teacher_name || 'Teacher';
  const location = useMemo(() => getLocation(classroom), [classroom]);
  const updated = formatUpdatedAt(classroom.updated_at);
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const profileUrl = classroom.share_code ? `/t/${classroom.share_code}` : '#';
  const showAvatar = Boolean(avatarUrl && !avatarFailed);

  return (
    <Link
      to={profileUrl}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border ${themeStyle.border} bg-white/90 shadow-soft transition hover:-translate-y-0.5 hover:shadow-md ${className}`.trim()}
    >
      {/* Header with accent color */}
      <div
        className="h-2 w-full"
        style={{ backgroundColor: classroom.accent_color || '#3b82f6' }}
      />

      <div className="relative flex items-center gap-3 p-4">
        {showAvatar ? (
          <img
            src={avatarUrl!}
            alt={`${teacherName}'s avatar`}
            className="h-14 w-14 rounded-full border-2 border-white bg-white object-cover shadow-sm"
            loading="lazy"
            onError={(event) => {
              setAvatarFailed(true);
              event.currentTarget.src = PLACEHOLDER_AVATAR;
            }}
          />
        ) : (
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full text-white"
            style={{ backgroundColor: classroom.accent_color || '#3b82f6' }}
          >
            <span className="text-lg font-semibold">{initials}</span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-cocoa group-hover:text-cocoa/80 line-clamp-1">
            {displayName}
          </p>
          <p className="mt-0.5 text-sm text-cocoa/70 line-clamp-1">{teacherName}</p>
          {classroom.grade_level && (
            <span
              className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
              style={{ backgroundColor: classroom.accent_color || '#3b82f6' }}
            >
              {classroom.grade_level}
            </span>
          )}
        </div>
      </div>

      {/* About preview */}
      {classroom.about && (
        <div className="px-4 pb-2">
          <p className="text-sm text-cocoa/70 line-clamp-2">{classroom.about}</p>
        </div>
      )}

      {/* Location */}
      {location && (
        <div className="px-4 pb-2">
          <p className="text-xs text-cocoa/50 line-clamp-1">{location}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto border-t border-sand/60 bg-sand/20 p-3">
        <div className="flex items-center justify-between text-xs text-cocoa/70">
          <span className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            Classroom
          </span>
          {updated && <span>Updated {updated}</span>}
        </div>
      </div>
    </Link>
  );
}
